import { useUserContext } from "@contexts/UserContext/index.context";
import { Alert, Box, CircularProgress, Grid, Typography } from "@mui/material";
import { UserRank } from "@types_def/models/user.type";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import MemberForm from "@/components/organization/Member/Form";
import OrganizationMembersTable from "@/components/organization/organization-members-table";
import { useAddMemberToOrganizationMutation } from "@/libs/generated/hooks";
import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";
import { MemberFormSchemaDefaultValues } from "@/schemas/organization-form/member.schema";

function ClientOrganization() {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const userRank = userState?.["custom:rank"];
  const organizationId = userState?.organizationId;

  const {
    mutateAsync: addMemberToOrganization,
    isPending,
    error,
  } = useAddMemberToOrganizationMutation();

  // Determine form configuration based on user rank
  const getFormConfig = () => {
    const disabledFields: Array<keyof MemberFormSchemaType> = [];
    const values = { ...MemberFormSchemaDefaultValues };

    // Organization owners can assign any organization rank
    if (userRank === UserRank.ORGANIZATION_OWNER) {
      values.assigneeRank = UserRank.ORGANIZATION_MANAGER;
    }
    // Organization managers can only assign member rank
    else if (userRank === UserRank.ORGANIZATION_MANAGER) {
      disabledFields.push("assigneeRank");
      values.assigneeRank = UserRank.ORGANIZATION_MEMBER;
    }
    // Organization members can only assign member rank
    else {
      disabledFields.push("assigneeRank");
      values.assigneeRank = UserRank.ORGANIZATION_MEMBER;
    }

    return { disabledFields, values };
  };

  const { disabledFields, values } = getFormConfig();

  const onSubmit = async (data: MemberFormSchemaType) => {
    if (organizationId == null || organizationId === "") {
      toast.error(t("network.errors.400"), {
        position: "bottom-right",
      });
      return;
    }

    try {
      const result = await addMemberToOrganization({
        AddMemberToOrganizationInput: {
          organizationId,
          assigneeFirstName: data.assigneeFirstName,
          assigneeLastName: data.assigneeLastName,
          assigneeEmail: data.assigneeEmail,
          assigneePhone: data.assigneePhone,
          assigneeFunction: data.assigneeFunction,
          assigneeService: data.assigneeService,
          assigneeRank: String(data.assigneeRank),
        },
      });

      if (result.addMemberToOrganization != null) {
        toast.success(t("network.organization.member.created"), {
          position: "bottom-right",
        });
      }
    } catch (_err) {
      // Error is handled by the mutation hook and displayed in the UI
    }
  };

  if (organizationId == null || organizationId === "") {
    return (
      <Box p={4}>
        <Alert severity="error">{t("network.errors.404")}</Alert>
      </Box>
    );
  }
  return (
    <Box p={4}>
      <Grid container spacing={2}>
        <Grid container size={{ xs: 12, md: 4 }} spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
              {t("organization.details.title")}
            </Typography>
            {/* Organization details would go here if needed */}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h5" sx={{ mb: 4 }}>
              {t("organization.actions.add-member.title")}
            </Typography>

            {error != null ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error instanceof Error ? error.message : t("network.errors.unknown")}
              </Alert>
            ) : null}

            {isPending && (
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <CircularProgress size={20} />
                <span style={{ marginLeft: 8 }}>{t("common.loading")}</span>
              </Box>
            )}

            <MemberForm
              disabledFields={disabledFields}
              unUsedFields={["id"]}
              values={values}
              onSubmit={onSubmit}
              title="user-info"
              label={t("organization.actions.add-member.text")}
            />
          </Grid>
        </Grid>

        <Grid container size={{ xs: 12, md: 8 }} spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t("layouts.sub-headers.organization.members")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <OrganizationMembersTable memberRank={UserRank.ORGANIZATION_MEMBER} />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ClientOrganization;
